import { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { InvoiceGenericItem, InvoiceGenericItemType } from '../models/invoice-generic-item';
import { InvoiceProductItem } from '../models/invoice-product-item';
import { Product } from '../../../common/models/product';
import { TaxRate } from '../../../common/models/tax-rate';
import { useAppSelector } from '../../../store/use-appstate-selector';

interface InvoiceDetailValue {
    /**
     * The supplier as the head of the document has it right now — undefined
     * while the head is not on screen, null when none is chosen.
     *
     * The article search has to follow the field, not the saved document:
     * since head and positions stand on one page, a new document has nothing
     * saved yet and a changed one would filter on the supplier it had before
     * (Prüfbericht Roland Niedermayr, 28.09.2026).
     */
    supplierAccountId?: number | null;
    setSupplierAccountId?: (id: number | null | undefined) => void;
    /** Its name, for the message when a search finds nothing. */
    supplierAccountName?: string;
    setSupplierAccountName?: (name: string | undefined) => void;
    /**
     * The country of the invoice address as the head has it right now, by the
     * same rule: the positions book goods by it, and a new invoice has no
     * saved address to read it from (#2263).
     */
    invoiceCountryCode?: string | null;
    setInvoiceCountryCode?: (code: string | null | undefined) => void;
    /** How many positions the document carries right now, and how to empty it. */
    positionCount: number;
    setPositionCount?: (count: number) => void;
    removeAllPositions?: () => void;
    setRemoveAllPositions?: (remove: (() => void) | undefined) => void;
    showAddPositionModal: boolean;
    setShowAddPositionModal?: (show: boolean) => void;
    showNewTextItemModal: boolean;
    setShowNewTextItemModal?: (show: boolean) => void;
    isNewTextItem: boolean;
    setIsNewTextItem?: (isNew: boolean) => void;
    selectedProduct: Product | null;
    setSelectedProduct?: (product: Product | null) => void;
    invoiceItems: (InvoiceProductItem | InvoiceGenericItem)[];
    setInvoiceItems?: (items: (InvoiceProductItem | InvoiceGenericItem)[]) => void;
    netShippingCosts: number;
    setNetShippingCosts?: (costs: number) => void;
    netSurcharge: number;
    setNetSurcharge?: (surcharge: number) => void;
    isTaxFree: boolean;
    setIsTaxFree?: (isTaxFree: boolean) => void;
    showTaxRateOverrideModal: boolean;
    setShowTaxRateOverrideModal?: (show: boolean) => void;
    taxRateForOverride?: TaxRate;
    setTaxRateForOverride?: (taxRate: TaxRate | undefined) => void;
    showEditPositionModal: boolean;
    setShowEditPositionModal?: (show: boolean) => void;
    showGenericItemModal: { show: boolean; type: InvoiceGenericItemType };
    setShowGenericItemModal?: ({ show, type }: { show: boolean; type: InvoiceGenericItemType }) => void;
}

export const InvoiceDetailContext = createContext<InvoiceDetailValue>({
    positionCount: 0,
    showAddPositionModal: false,
    showNewTextItemModal: false,
    isNewTextItem: false,
    selectedProduct: null,
    invoiceItems: [],
    netShippingCosts: 0,
    netSurcharge: 0,
    isTaxFree: false,
    showTaxRateOverrideModal: false,
    taxRateForOverride: undefined,
    showEditPositionModal: false,
    setShowEditPositionModal: undefined,
    showGenericItemModal: { show: false, type: InvoiceGenericItemType.Surcharge },
});

interface InvoiceDetailProviderProps {
    children: ReactNode;
    onChange: (netShippingCosts: number, netSurcharge: number, isTaxFree: boolean) => void;
}

export const InvoiceDetailProvider = ({ children, onChange }: InvoiceDetailProviderProps) => {
    const [supplierAccountId, setSupplierAccountId] = useState<number | null | undefined>(undefined);
    const [supplierAccountName, setSupplierAccountName] = useState<string | undefined>(undefined);
    const [invoiceCountryCode, setInvoiceCountryCode] = useState<string | null | undefined>(undefined);
    const [positionCount, setPositionCount] = useState(0);
    const [removeAllPositions, setRemove] = useState<{ run?: () => void }>({});
    const [showAddPositionModal, setShowAddPositionModal] = useState(false);
    const [showAddTextItemModal, setShowAddTextItemModal] = useState(false);
    const [isNewTextItem, setIsNewTextItem] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [invoiceItems, setInvoiceItems] = useState<(InvoiceProductItem | InvoiceGenericItem)[]>([]);
    const [netShippingCosts, setNetShippingCosts] = useState(0);
    const [netSurcharge, setNetSurcharge] = useState(0);
    const [isTaxFree, setIsTaxFree] = useState(false);
    const [taxRateForOverride, setTaxRateForOverride] = useState<TaxRate | undefined>(undefined);
    const [showTaxRateOverrideModal, setShowTaxRateOverrideModal] = useState(false);
    const [showEditPositionModal, setShowEditPositionModal] = useState(false);
    const [showGenericItemModal, setShowGenericItemModal] = useState<{ show: boolean; type: InvoiceGenericItemType }>({
        show: false,
        type: InvoiceGenericItemType.Surcharge,
    });

    // The summary panel edits these values, so they must start from the loaded
    // invoice instead of zero; otherwise a save before the positions table has
    // rendered sends zeros (#2218).
    const loaded = useAppSelector(state => state.invoiceDetail.positions.stepData.values);

    useEffect(() => {
        setNetShippingCosts(loaded.netShippingCosts);
        setNetSurcharge(loaded.netSurcharge);
        setIsTaxFree(loaded.isTaxFree);
    }, [loaded.netShippingCosts, loaded.netSurcharge, loaded.isTaxFree]);

    useEffect(() => {
        onChange(netShippingCosts, netSurcharge, isTaxFree);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [netShippingCosts, netSurcharge, isTaxFree]);

    return (
        <InvoiceDetailContext.Provider
            value={{
                supplierAccountId,
                setSupplierAccountId,
                supplierAccountName,
                setSupplierAccountName,
                invoiceCountryCode,
                setInvoiceCountryCode,
                positionCount,
                setPositionCount,
                removeAllPositions: removeAllPositions.run,
                setRemoveAllPositions: run => setRemove({ run }),
                showAddPositionModal,
                setShowAddPositionModal,
                showNewTextItemModal: showAddTextItemModal,
                setShowNewTextItemModal: setShowAddTextItemModal,
                isNewTextItem,
                setIsNewTextItem,
                selectedProduct,
                setSelectedProduct,
                invoiceItems,
                setInvoiceItems,
                netShippingCosts,
                setNetShippingCosts,
                netSurcharge,
                setNetSurcharge,
                isTaxFree,
                setIsTaxFree,
                showTaxRateOverrideModal,
                setShowTaxRateOverrideModal,
                taxRateForOverride,
                setTaxRateForOverride,
                showEditPositionModal,
                setShowEditPositionModal,
                showGenericItemModal,
                setShowGenericItemModal,
            }}
        >
            {children}
        </InvoiceDetailContext.Provider>
    );
};

export const useInvoiceDetailContext = () => {
    return useContext(InvoiceDetailContext);
};
